import { z } from 'zod'

import { calendarDateInTimeZone } from '../utils/calendar-date'
import { BillingInterval } from './subscription'

/**
 * No-card trials (docs/PLAN-no-card-trials.md).
 *
 * A cardless trial is a local subscription row (status `trialing`, placeholder
 * `sub_free_` / `sub_internal_` Stripe ids) for a paid plan. It never becomes a
 * Stripe object until an owner or admin adds a card. The phase is derived from
 * the row's dates by `resolveTrialPhase` — the same function the API guard, the
 * lifecycle sweep and the clients rely on.
 */

/** Grace period between trial end and the read-only lock. */
export const TRIAL_GRACE_DAYS = 7

/** Error code the API returns (HTTP 403) for writes to a locked workspace. */
export const WORKSPACE_READ_ONLY_ERROR_CODE = 'WORKSPACE_READ_ONLY'

/** Error code for a trial start the organization is not eligible for. */
export const TRIAL_UNAVAILABLE_ERROR_CODE = 'TRIAL_UNAVAILABLE'

/**
 * Error code (HTTP 409) for an ordinary Checkout requested by a workspace on
 * a no-card trial: it must add its card through trial activation
 * (`POST .../trial/activate`), which keeps its data and remaining days.
 */
export const TRIAL_ACTIVATION_REQUIRED_ERROR_CODE = 'TRIAL_ACTIVATION_REQUIRED'

export enum TrialPhase {
  /** Before trial end: full access, countdown. */
  TRIALING = 'TRIALING',
  /** Trial ended without a card: full access for the grace period. */
  GRACE = 'GRACE',
  /** Grace ended: data kept, viewing and export only. */
  LOCKED = 'LOCKED',
}

/** Milestones the lifecycle sweep notifies owners/admins about, once each. */
export enum TrialNoticeMilestone {
  TRIAL_ENDS_IN_7_DAYS = 'trial_t7',
  TRIAL_ENDS_IN_3_DAYS = 'trial_t3',
  TRIAL_ENDS_TOMORROW = 'trial_t1',
  TRIAL_ENDED = 'trial_ended',
  GRACE_ENDS_IN_2_DAYS = 'grace_t2',
  LOCKED = 'locked',
}

const DAY_MS = 24 * 60 * 60 * 1000

/** The Stripe-id prefixes of subscription rows that have no Stripe object. */
export function isInternalStripeSubscriptionId(
  stripeSubscriptionId?: string | null,
): boolean {
  return Boolean(
    stripeSubscriptionId?.startsWith('sub_free_') ||
    stripeSubscriptionId?.startsWith('sub_internal_'),
  )
}

export interface CardlessTrialCandidate {
  status?: string | null
  stripeSubscriptionId?: string | null
  planKey?: string | null
  trialEnd?: Date | string | null
}

/**
 * A paid-plan trial with no card behind it and a deadline. Rows without a
 * `trialEnd` (legacy admin grants) have no countdown and are never locked.
 */
export function isCardlessTrial(row: CardlessTrialCandidate): boolean {
  return (
    (row.status ?? '').toLowerCase() === 'trialing' &&
    Boolean(row.planKey) &&
    row.planKey !== 'free' &&
    isInternalStripeSubscriptionId(row.stripeSubscriptionId) &&
    toDate(row.trialEnd) !== null
  )
}

function toDate(value?: Date | string | null): Date | null {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(value)
  return Number.isFinite(date.getTime()) ? date : null
}

/**
 * Phase of a cardless trial.
 *
 * LOCKED requires a *persisted* grace end: the sweep writes it when it moves
 * the organization into grace and tells its admins. A late or disabled sweep
 * can therefore only delay a lock, never cause one nobody was warned about.
 */
export function resolveTrialPhase(input: {
  trialEnd: Date | string
  graceEndsAt?: Date | string | null
  now?: Date
}): TrialPhase {
  const now = input.now ?? new Date()
  const trialEnd = toDate(input.trialEnd)
  if (!trialEnd || now.getTime() < trialEnd.getTime())
    return TrialPhase.TRIALING
  const graceEndsAt = toDate(input.graceEndsAt)
  if (graceEndsAt && now.getTime() >= graceEndsAt.getTime()) {
    return TrialPhase.LOCKED
  }
  return TrialPhase.GRACE
}

/** When a trial that ends at `trialEnd` and is first seen expired at `now` stops being editable. */
export function computeGraceEndsAt(trialEnd: Date, now = new Date()): Date {
  return new Date(
    Math.max(trialEnd.getTime(), now.getTime()) + TRIAL_GRACE_DAYS * DAY_MS,
  )
}

/** The grace end to display before the sweep has persisted one. */
export function effectiveGraceEndsAt(
  trialEnd: Date | string,
  graceEndsAt?: Date | string | null,
  now = new Date(),
): Date {
  return toDate(graceEndsAt) ?? computeGraceEndsAt(toDate(trialEnd) ?? now, now)
}

/**
 * Whole calendar days from today to the deadline's day, both taken in the
 * organization's timezone. 0 means "today is the last day"; never negative.
 */
export function calendarDaysUntil(
  deadline: Date | string,
  timeZone: string,
  now = new Date(),
): number {
  const end = toDate(deadline)
  if (!end) return 0
  const zone = safeZone(timeZone)
  const endDay = calendarDateInTimeZone(end, zone)
  const today = calendarDateInTimeZone(now, zone)
  if (!endDay || !today) return 0
  const diff = Math.round(
    (Date.parse(`${endDay}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) /
      DAY_MS,
  )
  return Math.max(0, diff)
}

function safeZone(timeZone: string): string {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone })
    return timeZone
  } catch {
    return 'UTC'
  }
}

/**
 * The lifecycle notice due for a trial right now, if any. Keys include the
 * deadline so an admin trial extension re-arms them.
 */
export function dueTrialNotice(input: {
  phase: TrialPhase
  daysRemaining: number
  trialEnd: Date
  graceEndsAt: Date | null
}): { milestone: TrialNoticeMilestone; key: string } | null {
  const trialKey = input.trialEnd.toISOString()
  const graceKey = input.graceEndsAt?.toISOString() ?? trialKey
  let milestone: TrialNoticeMilestone | null = null
  let deadlineKey = trialKey
  if (input.phase === TrialPhase.TRIALING) {
    if (input.daysRemaining === 7)
      milestone = TrialNoticeMilestone.TRIAL_ENDS_IN_7_DAYS
    else if (input.daysRemaining === 3)
      milestone = TrialNoticeMilestone.TRIAL_ENDS_IN_3_DAYS
    else if (input.daysRemaining === 1)
      milestone = TrialNoticeMilestone.TRIAL_ENDS_TOMORROW
  } else if (input.phase === TrialPhase.GRACE) {
    // "Trial ended" is about the trial deadline; the grace reminder about the
    // grace deadline (so a re-granted grace re-arms only the latter).
    if (input.daysRemaining <= 2 && input.graceEndsAt) {
      milestone = TrialNoticeMilestone.GRACE_ENDS_IN_2_DAYS
      deadlineKey = graceKey
    } else {
      milestone = TrialNoticeMilestone.TRIAL_ENDED
    }
  } else if (input.phase === TrialPhase.LOCKED) {
    deadlineKey = graceKey
    milestone = TrialNoticeMilestone.LOCKED
  }
  return milestone ? { milestone, key: `${milestone}:${deadlineKey}` } : null
}

/**
 * Trial state as the clients see it, attached to the organization's
 * subscription payload (`OrganizationSubscription.trial`).
 */
export interface WorkspaceTrialStatus {
  phase: TrialPhase
  planKey: string
  planName: string
  /** ISO instant the trial ends. */
  trialEndsAt: string
  /** ISO instant the grace period ends (estimated until the sweep persists it). */
  graceEndsAt: string | null
  /**
   * Calendar days (organization timezone) left in the current phase:
   * to the trial end in TRIALING, to the grace end in GRACE, 0 when LOCKED.
   */
  daysRemaining: number
  /** True when writes are refused (LOCKED). */
  isReadOnly: boolean
  /** True when activating now keeps the remaining trial days (Stripe needs ≥48h). */
  canPreserveTrial: boolean
  /** Staff return trials never begin paid billing before this trial ends. */
  billingRequiresTrialEnd?: boolean
  /** Up to three owner/admin display names members can ask. */
  activationContacts: string[]
}

export const StartTrialDtoSchema = z.object({
  planId: z.string().uuid(),
  seats: z.number().int().min(1).max(10000).optional(),
  billingInterval: z.nativeEnum(BillingInterval).optional(),
})
export type StartTrialDto = z.infer<typeof StartTrialDtoSchema>

/**
 * True when `value` is an absolute http(s) URL on one of `allowedOrigins`
 * (e.g. the web app's WEB_URL). Used for the activation return URL, which
 * becomes Stripe Checkout's success/cancel redirect: any other origin would
 * turn Checkout into an open redirect.
 */
export function isAllowedReturnUrl(
  value: string,
  allowedOrigins: readonly string[],
): boolean {
  let url: URL
  try {
    url = new URL(value)
  } catch {
    return false
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return false
  if (url.username || url.password) return false
  const origins = new Set(
    allowedOrigins.flatMap(origin => {
      try {
        return [new URL(origin).origin]
      } catch {
        return []
      }
    }),
  )
  return origins.has(url.origin)
}

export const ActivateTrialDtoSchema = z.object({
  // The API additionally requires one of its configured app origins
  // (isAllowedReturnUrl); only absolute http(s) URLs parse here.
  returnUrl: z
    .string()
    .url()
    .refine(value => /^https?:\/\//i.test(value), {
      message: 'returnUrl must be an http(s) URL',
    }),
  planId: z.string().uuid().optional(),
  seats: z.number().int().min(1).max(10000).optional(),
  billingInterval: z.nativeEnum(BillingInterval).optional(),
})
export type ActivateTrialDto = z.infer<typeof ActivateTrialDtoSchema>
